import React, { useContext } from "react";
import { Image, Divider, Form, Input, Button, Row, Col, Upload, Card, Avatar } from 'antd';
import { UserOutlined } from "@ant-design/icons";
import Location from "components/widgets/modals/Location";
import { ModalContext } from "context/ModalContext";
import { greeting } from "utils/helpers";
import "assets/css/style.min.css";
import "assets/css/app.min.css";
import CountCard from "components/count_card/CountCardContainer";
import TransactionHistory from "components/transaction_history/TransactionHistoryWrapper";
import RecentRequests from "components/recent_requests/RecentRequestsContainer";
import { api_srv } from "services";
import WalletContainer from "pages/accountManagement/WalletContainer";
import AreaChart from "components/chart/AreaChart";
import { useQuery } from "react-query";
import { getDashSummary } from "utils/features/common";

export default function Dashboard({ data }) {
  const { setModalShow, modalShow, modalClose } = useContext(ModalContext);

  async function fetch_intermediary_details() {
    let resp = await (await api_srv).fetchIntermediaryProfile();
    return resp.data;
  }

  const {
    data: details,
    isLoading: detailsLoading,
    isError: detailsError,
    isSuccess: detailsLoaded,
  } = useQuery("profile details", fetch_intermediary_details);

  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
    isSuccess: summaryLoaded,
  } = useQuery("dashsummary", getDashSummary);



  // Extract broker info
  const broker = details && details.length > 0 ? details[0] : null;
  const imgSrc = broker?.profileImage;
  const brokerName = broker?.intermediaryNames || "Broker Profile";
  const category = broker?.category || "Unknown";
  const primaryEmail = broker?.primaryEmail || "";
  const licenseNumber = broker?.licenseNumber || "";
  const description = broker?.description || "";
  const kycStatus = broker?.kycStatus || "Unknown";

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map(n => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const renderProfileImage = () => {
    if (detailsLoading) {
      return (
        <div className="flex items-center justify-center bg-gray-200 animate-pulse #rounded-full w-20 h-20">
          <div className="w-10 h-10 bg-gray-400 #rounded-full animate-pulse"></div>
        </div>
      );
    }

    return (
      <div style={{ position: "relative" }}>
        <div style={{ display: 'flex', justifyContent: 'center'}}>
          {!!imgSrc ? (
            <img
              src={imgSrc}
              alt={`${brokerName} profile`}
              style={{
                width: "100%",
                height: "12em",
                borderRadius: "2%",
                objectFit: "cover",
                border: "3px solid #fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.nextSibling.style.display = "flex";
              }}
            />
          ) : (
            <Avatar
              size={80}
              className="absolute top-0 left-0 bg-gradient-to-br from-purple-500 to-indigo-600 text-white font-semibold"
            >
              {getInitials(brokerName)}
            </Avatar>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: "1rem" }}>
      {/* Main Layout Container */}
      <div
        className="dashboard-columns"
        style={{
          display: "flex",
          gap: "1rem",
          maxWidth: "100%",
          margin: "0 auto"
        }}
      >
        {/* LEFT COLUMN - 70% */}
        <div
          className="dashboard-left-col"
          style={{
            flex: "0 0 70%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          {/* Count Cards */}
          <Card
            style={{
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              flexShrink: 0
            }}
            bodyStyle={{ padding: "1.5rem" }}
          >
            {summaryLoaded && (<CountCard data={summary}/>)}
            
          </Card>

          {/* Area Chart */}
          <Card
            style={{
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              flex: 1,
              minHeight: "300px",
              height: "100%"
            }}
            bodyStyle={{ 
              padding: "1.5rem", 
              height: "100%",
              display: "flex",
              flexDirection: "column" 
            }}
          >
            <div style={{ height: "100%", minHeight: '250px', flex: 1 }}>
              {summaryLoaded && (<AreaChart data={summary}/>)}
            </div>
          </Card>
        </div>

        {/* RIGHT COLUMN - Broker Profile - 30% */}
        <div
          className="dashboard-right-col"
          style={{
            flex: "0 0 30%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <Card
            title={
              <div className="flex items-center gap-3">
                {renderProfileImage()}
                <div>
                  <div>
                    <h3>
                      Name: {brokerName}
                    </h3>
                  </div>

                  <div>
                    <p style={{ margin: "4px 0 0 0", color: "#666", fontSize: "0.9rem" }}>
                      Category: {category}
                    </p>
                    <div>
                      <p style={{ margin: "4px 0 0 0", color: "#666", fontSize: "0.9rem" }}>
                        License: {licenseNumber}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            }
            style={{
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: "400px"
            }}
            bodyStyle={{
              padding: "1rem",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%"
            }}
          >
            <div style={{ 
              flex: 1, 
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "1rem"
            }}>
              {/* Description */}
              <div style={{ flexShrink: 0 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M3.38751 8.69074C1.5375 10.5235 1.5375 13.48 3.38751 15.3127L8.69548 20.6207C10.5282 22.4534 13.5021 22.4534 15.3348 20.6207L20.6255 15.3127C22.4582 13.48 22.4582 10.5062 20.6255 8.67345L15.3175 3.36548C13.4848 1.55005 10.5282 1.55005 8.69548 3.36548L3.38751 8.69074ZM14.332 14.3272C15.6114 13.0478 15.6114 10.973 14.332 9.67626C13.0525 8.39682 10.9777 8.39682 9.681 9.67626C8.40156 10.9557 8.40156 13.0305 9.681 14.3272C10.9605 15.6067 13.0525 15.6067 14.332 14.3272Z" fill="#323544"/>
                </svg>
                {description && (
                  <p style={{
                    color: "#666",
                    lineHeight: "1.5",
                    marginBottom: "1rem",
                    wordWrap: "break-word",
                    overflowWrap: "break-word"
                  }}>
                    {description}
                  </p>
                )}
              </div>

              {/* Status Badges */}
              <div style={{ 
                display: "flex", 
                gap: "0.5rem", 
                flexWrap: "wrap", 
                marginBottom: "1rem",
                flexShrink: 0 
              }}>
                <span style={{
                  padding: "4px 12px",
                  backgroundColor: kycStatus.includes("PENDING") ? "#fff3cd" : "#d4edda",
                  color: kycStatus.includes("PENDING") ? "#856404" : "#155724",
                  borderRadius: "20px",
                  fontSize: "0.8rem",
                  border: `1px solid ${kycStatus.includes("PENDING") ? "#ffeaa7" : "#c3e6cb"}`
                }}>
                  KYC: {kycStatus}
                </span>
                <span style={{
                  padding: "4px 12px",
                  backgroundColor: broker?.status === "ACTIVE" ? "#d4edda" : "#f8d7da",
                  color: broker?.status === "ACTIVE" ? "#155724" : "#721c24",
                  borderRadius: "20px",
                  fontSize: "0.8rem",
                  border: `1px solid ${broker?.status === "ACTIVE" ? "#c3e6cb" : "#f5c6cb"}`
                }}>
                  Status: {broker?.status || "Unknown"}
                </span>
              </div>

              <Divider style={{ margin: "0.75rem 0", flexShrink: 0 }} />

              {/* Contact Info */}
              <div style={{ 
                display: "flex", 
                flexDirection: "column", 
                gap: "0.5rem",
                flexShrink: 0 
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ color: "#666", minWidth: "60px", fontSize: "0.85rem" }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M22 6.25649V17.25C22 18.4926 20.9926 19.5 19.75 19.5H4.25C3.00736 19.5 2 18.4926 2 17.25V6.23398C2 6.22372 2.00021 6.2135 2.00061 6.20334C2.01781 5.25972 2.78812 4.5 3.73592 4.5H20.2644C21.2229 4.5 22 5.27697 22.0001 6.23549C22.0001 6.24249 22.0001 6.24949 22 6.25649ZM3.5 8.187V17.25C3.5 17.6642 3.83579 18 4.25 18H19.75C20.1642 18 20.5 17.6642 20.5 17.25V8.18747L13.2873 13.2171C12.5141 13.7563 11.4866 13.7563 10.7134 13.2171L3.5 8.187ZM20.5 6.2286L20.5 6.23398V6.24336C20.4976 6.31753 20.4604 6.38643 20.3992 6.42905L12.4293 11.9867C12.1716 12.1664 11.8291 12.1664 11.5713 11.9867L3.60116 6.42885C3.538 6.38481 3.50035 6.31268 3.50032 6.23568C3.50028 6.10553 3.60577 6 3.73592 6H20.2644C20.3922 6 20.4963 6.10171 20.5 6.2286Z" fill="#323544"/>
                    </svg>
                  </span>
                  <a
                    href={`mailto:${primaryEmail}`}
                    style={{
                      color: "#1890ff",
                      fontSize: "0.85rem",
                      wordBreak: "break-all"
                    }}
                  >
                    {primaryEmail || "Not provided"}
                  </a>
                </div>
                {broker?.contacts && broker.contacts.length > 0 && (
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                    <span style={{ color: "#666", minWidth: "60px", fontSize: "0.85rem" }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 2C6.75736 2 5.75 3.00736 5.75 4.25V19.75C5.75 20.9926 6.75736 22 8 22H16C17.2426 22 18.25 20.9926 18.25 19.75V4.25C18.25 3.00736 17.2426 2 16 2H8ZM7.25 4.25C7.25 3.83579 7.58579 3.5 8 3.5H16C16.4142 3.5 16.75 3.83579 16.75 4.25V19.75C16.75 20.1642 16.4142 20.5 16 20.5H8C7.58579 20.5 7.25 20.1642 7.25 19.75V4.25Z" fill="#323544"/>
                        <path opacity="0.4" d="M11 17.5C10.5858 17.5 10.25 17.8358 10.25 18.25C10.25 18.6642 10.5858 19 11 19H13C13.4142 19 13.75 18.6642 13.75 18.25C13.75 17.8358 13.4142 17.5 13 17.5H11Z" fill="#323544"/>
                      </svg>
                    </span>
                    <span style={{
                      fontSize: "0.85rem",
                      wordBreak: "break-all"
                    }}>
                      {broker.contacts.join(", ")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Responsive Styles */}
      <style jsx>{`
        @media (max-width: 992px) {
          .dashboard-columns {
            flex-direction: column;
            gap: 1rem;
          }

          .dashboard-left-col,
          .dashboard-right-col {
            flex: 1 1 auto;
            width: 100%;
            min-height: auto;
          }

          .dashboard-left-col > .ant-card:first-child {
            order: 1;
          }

          .dashboard-left-col > .ant-card:nth-child(2) {
            order: 3;
            min-height: 350px;
            height: auto;
          }

          .dashboard-right-col > .ant-card {
            order: 2;
            min-height: 400px;
            height: auto;
          }
        }

        @media (max-width: 576px) {
          .dashboard-columns {
            gap: 0.75rem;
          }
          
          .dashboard-left-col > .ant-card:nth-child(2) {
            min-height: 300px;
          }
          
          .dashboard-right-col > .ant-card {
            min-height: 350px;
          }
        }
      `}</style>
    </div>
  );
}